import React, { memo, useCallback, useMemo } from 'react';
import { useExpanded, useFlexLayout, useSortBy, useTable } from 'react-table';

import { ReactTableWrapper } from 'ui-components/components/react-table-wrapper/react-table-wrapper';
import { Text, TextType } from 'ui-components/components/text';

import { BundleSubRow } from './bundle-sub-row/bundle-sub-row';
import { getColumns } from './columns/columns';
import { messages } from './messages';
import styles from './styles.module.less';

/**
 * @typedef {import('../types').ScannedProduct} ScannedProduct
 */

const getBodyRowClassName = () => styles['header-row'];

/**
 * @typedef {object} ScanToReceivetableProps
 * @property {string} warehouseId
 * @property {boolean} showAdvancedColumns
 * @property {boolean} showLocationErrors
 * @property {boolean} isUnitsOfMeasureEnabled
 * @property {Array<ScannedProduct>} scannedProducts
 * @property {React.Dispatch<React.SetStateAction<Array<ScannedProduct>>>} setScannedProducts
 */

/**
 * @type {React.NamedExoticComponent<ScanToReceivetableProps>}
 * @returns {React.ReactNode}
 */
export const ScanToReceiveTable = memo(
  ({ scannedProducts, setScannedProducts, warehouseId, showAdvancedColumns, showLocationErrors, isUnitsOfMeasureEnabled = false }) => {
    const removeProduct = useCallback(
      (rowKey) => {
        setScannedProducts((prev) => prev.filter((product) => (product.rowKey ?? String(product.productId)) !== rowKey));
      },
      [setScannedProducts],
    );

    const updateProduct = useCallback(
      (rowKey, property, value) => {
        setScannedProducts((old) =>
          old.map((product) => {
            if ((product.rowKey ?? String(product.productId)) === rowKey) {
              return {
                ...product,
                [property]: value,
              };
            }
            return product;
          }),
        );
      },
      [setScannedProducts],
    );

    const updateProductUom = useCallback(
      (rowKey, uomId, uomTypeName, conversionFactor, eaches) => {
        setScannedProducts((old) => {
          const target = old.find((p) => (p.rowKey ?? String(p.productId)) === rowKey);
          if (!target) return old;

          const newRowKey = isUnitsOfMeasureEnabled && uomId > 0 ? `${target.productId}_${uomId}` : String(target.productId);
          const collidingKey = newRowKey !== rowKey ? newRowKey : null;
          const collidingRow = collidingKey ? old.find((p) => (p.rowKey ?? String(p.productId)) === collidingKey) : undefined;

          const updated = {
            ...(collidingRow ?? target),
            uomId,
            uomTypeName,
            conversionFactor,
            eaches,
            rowKey: newRowKey,
            quantity: target.quantity + (collidingRow?.quantity ?? 0),
          };

          return old.reduce((acc, product) => {
            const key = product.rowKey ?? String(product.productId);
            if (key === rowKey) return [...acc, updated];
            if (collidingRow && key === collidingKey) return acc;
            return [...acc, product];
          }, []);
        });
      },
      [setScannedProducts, isUnitsOfMeasureEnabled],
    );

    const updateBundleComponent = useCallback(
      (bundleProductId, componentProductId, property, value) => {
        setScannedProducts((old) =>
          old.map((product) => {
            if (product.productId === bundleProductId && product.components) {
              return {
                ...product,
                components: product.components.map((component) => {
                  if (component.productId === componentProductId) {
                    return {
                      ...component,
                      [property]: value,
                    };
                  }
                  return component;
                }),
              };
            }
            return product;
          }),
        );
      },
      [setScannedProducts],
    );

    const columns = useMemo(
      () =>
        getColumns(
          warehouseId,
          showAdvancedColumns,
          updateProduct,
          updateProductUom,
          removeProduct,
          showLocationErrors,
          isUnitsOfMeasureEnabled,
        ),
      [removeProduct, showAdvancedColumns, showLocationErrors, updateProduct, updateProductUom, warehouseId, isUnitsOfMeasureEnabled],
    );

    const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable(
      {
        // @ts-expect-error ???
        columns,
        data: scannedProducts,
        getRowId: (row) => row.rowKey ?? row.productId.toString(),
        // @ts-expect-error - autoResetExpanded is a valid option for useExpanded plugin
        autoResetExpanded: false,
      },
      useSortBy,
      useFlexLayout,
      useExpanded,
    );

    const subRowProps = useMemo(
      () => ({
        updateBundleComponent,
        warehouseId,
        showAdvancedColumns,
        showLocationErrors,
      }),
      [showAdvancedColumns, showLocationErrors, updateBundleComponent, warehouseId],
    );

    return (
      <ReactTableWrapper
        className={styles['product-table']}
        emptyRow={<Text className={styles['empty-row']} textType={TextType.Paragraph} value={messages.noProductsScanned} />}
        getBodyRowClassName={getBodyRowClassName}
        getTableBodyProps={getTableBodyProps}
        getTableProps={getTableProps}
        headerGroups={headerGroups}
        headerRowClassName={styles['header-row']}
        prepareRow={prepareRow}
        renderSubRow={useCallback(
          (row) => (
            <BundleSubRow row={row} {...subRowProps} />
          ),
          [subRowProps],
        )}
        rows={rows}
      />
    );
  },
);
